/**
 * Every tunable of the chime simulation in one place. Units are SI (metres, seconds,
 * kilograms, newtons) unless a comment says otherwise. The wind is deliberately not
 * "honest": a real chime needs 5-10 m/s to ring, this one rings at 0.4-2.5 m/s so the
 * visitor sees a breeze, not a storm. `wind.forceScale` is that lie, made explicit.
 */
export const PHYS = {
  g: 9.81,
  airDensity: 1.2,

  tube: {
    /** k of lengthForFrequency: 10.6 keeps the lowest tube's bottom above tube.floorY */
    lengthK: 10.6,
    minLength: 0.45,
    maxLength: 1.05,
    minRadius: 0.010,
    maxRadius: 0.017,
    minCord: 0.10,
    maxCord: 0.22,
    /** the longest tube's bottom must stay above this world Y */
    floorY: 0.2,
    /** mass = massPerRadiusMetre * radius * length (thin-wall metal, ~1.5 mm) */
    massPerRadiusMetre: 45,
    /** structural + air damping on angular velocity, 1/s (amplitude halves in ~10 s) */
    damping: 0.14,
    dragCd: 1.0,
    /** slender tubes barely feel wind; this is the visual sway gain */
    dragScale: 8,
    /** safety net on |lean| (tangent slope). 1.0 = 45 degrees */
    maxLean: 1.0,
  },

  clapper: {
    radius: 0.09,
    mass: 0.30,
    /** edge-on drag area of the disc (2R * thickness) */
    dragArea: 0.0036,
    dragCd: 1.0,
    /** see sail.relVel */
    relVel: 0.5,
    /** cord friction etc., 1/s */
    damping: 0.06,
    /** fraction of the tube (0 top, 1 bottom) the disc should sit at; the cord is derived */
    strikeZone: [0.4, 0.6] as const,
    /**
     * Invisible soft fence: the disc centre is pushed back inside once it passes this
     * fraction of the ring ellipse, so it can never squeeze out between two tubes.
     */
    fence: 0.95,
    fenceStiffness: 400,
  },

  sail: {
    halfSize: 0.07,
    /** cord from the clapper centre to the sail centre */
    cord: 0.25,
    mass: 0.10,
    dragCd: 1.2,
    /** the sail is treated as a sphere of this radius for collisions with tubes */
    collisionRadius: 0.05,
    /**
     * Fraction of the sail's own velocity that counts in the relative wind. 1 is honest
     * quadratic drag, which with forceScale also scales the damping and pins the clapper
     * (Q ~ 2 in a gust: a creep and a rattle). Lower keeps the drive and lets it swing.
     */
    relVel: 0.6,
    /** the sail centre stays at least this far below the clapper centre (cord fraction) */
    minDrop: 0.35,
    /**
     * Emit strike events for sail-tube hits? The sail still collides (never passes through
     * a tube) but by default its knocks are silent: with them on, the idle rate doubles
     * into a constant soft clatter of dull thuds near the tube bottoms.
     */
    strikes: false,
    strikeVelocityScale: 0.6,
    strikeMinSpeed: 0.45,
    damping: 0.5,
  },

  wind: {
    /** steady breeze, m/s */
    breeze: 0.4,
    /** slow gust envelope period, s */
    slowPeriod: 22,
    /** extra speed the slow envelope adds at its peak (cubed, so peaks are brief) */
    slowBoost: 0.9,
    /** strong gust spacing (uniform), s */
    strongGapMin: 12,
    strongGapMax: 36,
    /** if nothing has rung for this long, the next strong gust is pulled forward and made strong */
    silenceLimit: 45,
    /** strong gust peak (uniform), m/s */
    strongPeakMin: 1.5,
    strongPeakMax: 2.6,
    strongDurationMin: 3,
    strongDurationMax: 8,
    /** gust envelope: fraction of the duration spent rising, and where the fall begins */
    strongRise: 0.08,
    strongFallFrom: 0.75,
    /** each strong gust is an eddy with its own direction: spread around the base, radians */
    gustDirSpread: 6.28,
    /** the mean speed never exceeds this (turbulence rides on top) */
    maxMeanSpeed: 2.6,
    /** prevailing direction wander, radians, on top of a slow veer round the compass */
    dirWander: 0.9,
    dirPeriod: 70,
    /** seconds for the prevailing direction to veer a full circle (0 = no veer) */
    veerPeriod: 300,
    /** turbulence peak as a fraction of the mean speed (rms is ~0.4 of this) */
    turbulence: 0.42,
    /** turbulence eddy size, m, and time scale, s (near the clapper's 1.4 s period) */
    turbScale: 1.3,
    turbTime: 1.5,
    /** normalises Simplex.curl2 output (max ~6, rms ~2.5) to a unit peak */
    curlNorm: 0.17,
    /** vertical component fraction */
    vertical: 0.12,
    /**
     * The aerodynamic gain on sail and clapper is DERIVED from the ring geometry: at a mean
     * wind of reachSpeed m/s the striker assembly's static lean reaches the median tube.
     * (~18 for the 0.34 x 0.16 ring, ~25 for a 0.26 circle.) gainTrim multiplies it.
     */
    reachSpeed: 2.2,
    gainTrim: 1.2,
  },

  contact: {
    restitution: 0.3,
    /** per-pair re-emission cooldown, s */
    cooldown: 0.08,
    /** relative normal speed that maps to velocity = 1 */
    velocityRef: 1.2,
    /** below this approach speed a touch is not a strike */
    minStrikeSpeed: 0.05,
    /** Baumgarte penetration correction: fraction of depth removed per step */
    bias: 0.12,
    slop: 0.0008,
    /** a contact ends only when the gap exceeds this (hysteresis against chatter) */
    releaseMargin: 0.004,
    /** lite tier: skip tube-tube pairs whose anchors are further apart than this */
    liteAnchorReach: 0.30,
  },

  hand: {
    /** hand speed above which it shoves, and below which it stops shoving (hysteresis) */
    moveSpeed: 0.15,
    stopSpeed: 0.08,
    /** hand.still above which it holds */
    stillTime: 0.15,
    /** per-(hand,tube) strike cooldown, s */
    cooldown: 0.12,
    /** strike velocity = clamp01(handSpeed * velocityScale / contact.velocityRef) */
    velocityScale: 0.6,
    /** fastest hand speed we believe for the strike velocity, m/s */
    maxSpeed: 2.5,
    /** the physical shove (entry impulse, follow) saturates here: a swipe brushes, it does not fling */
    shoveMaxSpeed: 1.2,
    /** shove: contact point is pulled toward this fraction of the hand velocity ... */
    follow: 0.6,
    /** ... at this rate, 1/s, only while the hand moves into the tube */
    followRate: 12,
    /** entry impulse: fraction of the hand velocity given to the contact point */
    entryGain: 0.35,
    /** a holding hand pinches: spring pulling the contact point to the hand, 1/s^2 */
    holdSpring: 50,
    /** a hand parts the curtain: small push away from the hand plane, fraction of speed */
    zPart: 0.25,
    /** damped rises at this rate while held, falls at this rate after release (1/s) */
    dampRise: 24,
    dampFall: 8,
    /** hold target when the hand is slow but not yet still */
    slowHold: 0.35,
    /** extra angular damping while held, 1/s per unit damped */
    holdDamping: 14,
    pressedHoldDamping: 22,
    /** DampEvent rate limit, s */
    dampEventInterval: 0.25,
  },

  energy: {
    kClapper: 0.9,
    kTube: 0.7,
    kHand: 0.6,
    tauBase: 2.4,
    tauSpread: 1.5,
    tauDamped: 0.15,
    /** mutual aid: shared partial within tolerance -> near, else far (audio uses the same) */
    aidNear: 0.12,
    aidFar: 0.02,
    aidTolerance: 0.03,
  },

  detune: {
    /** Ornstein-Uhlenbeck time constant, s */
    tau: 40,
    /** stationary standard deviation, cents (bounded by CONFIG.audio.detuneCents) */
    stdCents: 4,
  },
} as const;
